import React, { useEffect, useState } from "react";
import { StyleSheet } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  getHasAvatars,
  getUserId,
  getUserProfilePictureUrl,
} from "@microdoing/shared/services/user/selectors";
import { getHasCustomOnboarding } from "@microdoing/shared/services/client/selectors";
import { getHasOnboardingSurvey } from "@microdoing/shared/services/survey/selectors";
import i18n from "@microdoing/shared/i18n";
import { modifyUser } from "@microdoing/shared/services/user/actions";
import {
  Box,
  MainButton,
  MainText,
  PressableOpacity,
} from "app/components/common";
import { widthPercentageToDP } from "react-native-responsive-screen";
import FastImage from "react-native-fast-image";
import { useNavigation } from "@react-navigation/native";
import { getSignupRequesting } from "@microdoing/shared/services/signup/selectors";
import PictureComponent from "./PictureComponent";
import HeaderScreen from "app/components/common/HeaderScreen";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import { getProfilSettingsLoading } from "@microdoing/shared/services/profileSettings/selectors";
import { updateUserPicture } from "@microdoing/shared/services/profileSettings/actions";
import { resetUpload } from "@microdoing/shared/services/upload/actions";
import UploadProfilePicture from "app/scenes/UserProfile/UploadProfilePicture";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebSetupPicture from "./WebSetupPicture";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";

export const PICTURE_SIZE = 120;
const AVATAR_SIZE = 60;
const ENDPOINT = "https://s3.eu-west-3.amazonaws.com/liife-db";

const getRandomIntInclusive = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const avatarImages = [
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Cocotte.png",
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Francis.png",
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Lalie.png",
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Po.png",
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Kiki.png",
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Minou.png",
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Volant.png",
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/avatars/Croquette.png",
];

const styles = StyleSheet.create({
  avatarView: {
    width: isWebDesktop ? AVATAR_SIZE : widthPercentageToDP("20%"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    marginBottom: 15,
    marginHorizontal: "2%",
  },
  avatar: {
    height: AVATAR_SIZE,
    width: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
  },
});

export interface ISetupPictureProps {}

const SetupPicture = ({}: ISetupPictureProps) => {
  const [isModalPictureOpen, setIsModalPictureOpen] = useState(false);

  const picture = useSelector(getUserProfilePictureUrl);
  const hasCustomOnboarding = useSelector(getHasCustomOnboarding);
  const hasOnboardingSurvey = useSelector(getHasOnboardingSurvey);
  const requesting = useSelector(getSignupRequesting);
  const hasAvatars = useSelector(getHasAvatars);

  const files = useSelector(getFilesToUpload);
  const userId = useSelector(getUserId);
  const pictureUrl = useSelector(getUserProfilePictureUrl);
  const loading = useSelector(getProfilSettingsLoading);
  const insets = useSafeAreaInsets();

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const pictureFile = files?.[0];

  useEffect(() => {
    if (!!pictureFile) {
      const date = new Date().getTime();
      const uri = pictureFile?.uri?.replace("file://", "");
      dispatch(
        updateUserPicture({
          name: `${userId}-${date}`,
          uri,
          userId,
          picture: `${ENDPOINT}/${userId}-${date}`,
        }),
      );
      dispatch(resetUpload());
    }
  }, [pictureFile]);

  const goNext = () => {
    // default picture random if no picture or avatar selected
    if (!picture) {
      dispatch(
        modifyUser({
          picture:
            avatarImages[getRandomIntInclusive(0, avatarImages.length - 1)],
        }),
      );
    }

    if (hasOnboardingSurvey)
      return navigation.navigate("Survey", { isComingFromSignup: true });
    if (!hasCustomOnboarding) return navigation.navigate("SignupSliders");
    return navigation.navigate("WeeklyGoal", { isComingFromSignup: true });
  };

  const selectPhotoTapped = () => {
    setIsModalPictureOpen(true);
  };

  const onClearPicture = () => {
    dispatch(modifyUser({ picture: "" }));
  };

  const onPressAvatar = avatarSelected => () => {
    dispatch(modifyUser({ picture: avatarSelected }));
  };

  const renderBody = () => {
    return (
      <Box flex={1}>
        <UploadProfilePicture
          isModalVisible={isModalPictureOpen}
          setIsModalVisible={setIsModalPictureOpen}
        />
        <Box
          flex={1}
          marginBottom="s"
          justifyContent="center"
          testID="signup-picture-component"
        >
          <PictureComponent
            selectPhotoTapped={selectPhotoTapped}
            onClearPicture={onClearPicture}
            picture={pictureUrl}
            loading={loading}
          />
          <MainText
            marginTop="xl"
            textAlign="center"
            color="neutral.500"
            variant="l4-medium"
          >
            {i18n.t("download-or-select-avatar")}
          </MainText>
        </Box>
        {hasAvatars && (
          <Box
            width="100%"
            flexWrap="wrap"
            flexDirection="row"
            justifyContent="center"
          >
            {avatarImages.map(avatar => (
              <PressableOpacity
                key={avatar}
                onPress={onPressAvatar(avatar)}
                style={styles.avatarView}
              >
                <FastImage
                  source={{ uri: avatar }}
                  style={styles.avatar}
                  resizeMode="contain"
                />
              </PressableOpacity>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  if (isWebDesktop) {
    return <WebSetupPicture body={renderBody()} goNext={goNext} />;
  }

  return (
    <Box
      variant="screenView"
      backgroundColor="neutral.white"
      style={{ marginTop: insets.top, marginBottom: insets.bottom }}
    >
      <HeaderScreen leftIcon title={i18n.t("personalization")} />
      <UploadProfilePicture
        isModalVisible={isModalPictureOpen}
        setIsModalVisible={setIsModalPictureOpen}
      />
      {renderBody()}
      <Box width="100%" alignSelf="center">
        <MainButton
          height={normalize(56)}
          testID="signup-picture-next-button"
          isLoading={requesting}
          isDisabled={requesting}
          onPress={goNext}
          text={i18n.t("next")}
          textColor={requesting ? "neutral.700" : "neutral.white"}
        />
      </Box>
    </Box>
  );
};

export default SetupPicture;
